import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatDoctorName } from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Clock, CheckCircle, UserCheck } from "lucide-react";

const STATUS_FLOW = {
  waiting: { next: "in_consultation", label: "Start Consultation", color: "bg-blue-600 hover:bg-blue-700" },
  in_consultation: { next: "completed", label: "Complete", color: "bg-emerald-600 hover:bg-emerald-700" },
};
const STATUS_STYLES = {
  waiting: "bg-amber-100 text-amber-800",
  in_consultation: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
};

export default function DoctorDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [todayAppts, setTodayAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorId, setDoctorId] = useState(null);

  const fetchDoctorData = useCallback(async () => {
    try {
      // Find doctor by user email
      const { data: doctors } = await api.get("/doctors");
      const myDoctor = doctors.find(d => d.email === user?.email);
      if (myDoctor) {
        setDoctorId(myDoctor.id);
        const today = new Date().toISOString().split("T")[0];
        const { data } = await api.get(`/appointments?date=${today}&doctor_id=${myDoctor.id}`);
        setTodayAppts(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDoctorData();
  }, [fetchDoctorData]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      fetchDoctorData();
    } catch (err) {
      console.error(err);
    }
  };

  const counts = {
    waiting: todayAppts.filter(a => a.status === "waiting").length,
    in_consultation: todayAppts.filter(a => a.status === "in_consultation").length,
    completed: todayAppts.filter(a => a.status === "completed").length,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-sans">
          Good {new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 17 ? "Afternoon" : "Evening"}, {formatDoctorName(user?.name)}
        </h1>
        <p className="text-slate-500 text-sm mt-1">Here's your schedule for today</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Waiting", value: counts.waiting, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "In Consultation", value: counts.in_consultation, icon: UserCheck, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Completed", value: counts.completed, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
            <Icon size={22} className={color} />
            <div>
              <p className={`text-2xl font-bold font-sans ${color}`}>{value}</p>
              <p className="text-xs text-slate-600">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Today's Queue */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Calendar size={16} className="text-emerald-600" />
          <h2 className="text-base font-semibold text-slate-900 font-sans">Today's Patient Queue</h2>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-400">{t("loading")}</div>
        ) : todayAppts.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <Users size={32} className="mx-auto mb-2 opacity-30" />
            <p>No appointments today</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {todayAppts.map(appt => (
              <div key={appt.id} className={`flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors ${appt.is_emergency ? "bg-rose-50/30" : ""}`} data-testid={`doc-appt-${appt.id}`}>
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                  <span className="text-sm font-bold text-emerald-700">#{appt.token_number}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900 text-sm">{appt.patient_name}</p>
                    {appt.is_emergency && <Badge className="bg-rose-100 text-rose-700 text-xs">Emergency</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{appt.complaint || "General consultation"} • {appt.visit_type}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${STATUS_STYLES[appt.status]}`}>{appt.status?.replace("_", " ")}</Badge>
                  {STATUS_FLOW[appt.status] && (
                    <Button size="sm" className={`h-7 text-xs text-white ${STATUS_FLOW[appt.status].color}`} onClick={() => updateStatus(appt.id, STATUS_FLOW[appt.status].next)} data-testid={`doc-status-btn-${appt.id}`}>
                      {STATUS_FLOW[appt.status].label}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
